import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Quote,
  Video,
  Book,
  FileText,
  PenSquare,
  Sparkles,
  MessageSquare,
  Share2,
  ThumbsUp,
  Flame,
  Star,
  Clock,
  Tag,
  Youtube,
  Languages,
  Volume2,
  VolumeX,
  Reply,
  X,
} from "lucide-react";

import ApiService from "../../services/api-service";
import { FeedItem, Comment } from "../../types/feed";
import { CurrentUser } from "../../types/user";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Textarea } from "../ui/textarea";
import { showErrorToast, showSuccessToast, showInfoToast } from "../../lib/toast";
import { formatNumber, normalizePointsCount } from "../../lib/format";
import { resolveVoteAction, VoteState } from "../../lib/vote-logic";
import { cn } from "../../lib/utils";
import { translateText } from "../../lib/translate";
import { speakText, cancelSpeech } from "../../lib/speech";
import { useUrlLanguage, withLanguageParam } from "../../lib/url-state";
import { languageLabel, sourceLanguageOf, speechLocaleFor } from "../../lib/language";
import { LanguagePicker } from "../feed/LanguagePicker";
import { ArticleListenButton } from "../feed/ArticleListenButton";
import { Header } from "../layout/Header";
import { useTheme } from "../layout/theme-context";
import { PUBLIC_SITE_URL } from "../../constants/app";
type DetailedFeedItem = FeedItem & {
  tags?: string[];
  comments?: Comment[];
};

const heroMeta = {
  article: {
    label: "Article",
    icon: FileText,
    gradient: "bg-gradient-to-r from-slate-50 via-white to-slate-200",
  },
  book: {
    label: "Book",
    icon: Book,
    gradient: "bg-gradient-to-r from-amber-50 via-orange-50 to-white",
  },
  video: {
    label: "Video",
    icon: Video,
    gradient: "bg-gradient-to-r from-indigo-50 via-sky-50 to-white",
  },
  quote: {
    label: "Quote",
    icon: Quote,
    gradient: "bg-gradient-to-r from-rose-50 via-pink-50 to-white",
  },
  aiprompt: {
    label: "AI Prompt",
    icon: Sparkles,
    gradient: "bg-gradient-to-r from-emerald-50 via-cyan-50 to-white",
  },
  default: {
    label: "Post",
    icon: PenSquare,
    gradient: "bg-gradient-to-r from-slate-50 via-white to-slate-100",
  },
};

const highlightPills = [
  {
    label: "Community favorite",
    icon: Star,
    gradient: "from-amber-400/60 to-orange-500/40",
  },
  {
    label: "Daily spark",
    icon: Flame,
    gradient: "from-rose-500/50 to-pink-500/30",
  },
  {
    label: "Curated spark",
    icon: Sparkles,
    gradient: "from-sky-500/40 to-indigo-500/40",
  },
];

const DETAIL_CARD_BASE =
  "rounded-[32px] border border-slate-200/70 dark:border-slate-800/80 text-slate-900 dark:text-slate-100 transition-shadow duration-500 ease-out";
const DETAIL_CARD_CONTENT = "p-4 space-y-3";
const HERO_CARD_EXTRA =
  "bg-white/95 dark:bg-slate-900/70 dark:text-slate-50 shadow-[0_20px_70px_rgba(15,23,42,0.2)] backdrop-blur-xl transition-transform duration-500 ease-out hover:-translate-y-1";
const SNAPSHOT_CARD_EXTRA =
  "bg-white/90 dark:bg-slate-900/80 shadow-[0_16px_60px_rgba(15,23,42,0.2)] hover:shadow-[0_20px_65px_rgba(15,23,42,0.25)]";
const STORY_CARD_EXTRA =
  "bg-white/90 dark:bg-slate-900/80 shadow-[0_16px_50px_rgba(15,23,42,0.2)] hover:shadow-[0_20px_55px_rgba(15,23,42,0.25)]";
const DISCUSSION_CARD_EXTRA =
  "bg-white/90 dark:bg-slate-900/80 shadow-[0_16px_52px_rgba(15,23,42,0.22)] hover:shadow-[0_22px_60px_rgba(15,23,42,0.27)] h-full";

const formatDate = (value?: string | number) => {
  if (!value) return "Just now";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "Recently updated";
  }
};

const getStoredCurrentUser = (): CurrentUser | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem("inspirelens_user");
    return stored ? (JSON.parse(stored) as CurrentUser) : null;
  } catch {
    return null;
  }
};

const PostDetailPage = (props: any) => {
  const { header } = props;
  const { type, postId } = useParams<{ type: string; postId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState<DetailedFeedItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [localPoints, setLocalPoints] = useState<number>(0);
  const [voteState, setVoteState] = useState<VoteState>(0);
  const [isLiking, setIsLiking] = useState(false);
  const [commentText, setCommentText] = useState("");
  // The comment being replied to, if any.
  const [replyTo, setReplyTo] = useState<{ id: number; name: string } | null>(null);
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const [quoteLanguage, setQuoteLanguage, hasLangParam] = useUrlLanguage();
  // The language the quote was written in (quotes.language), e.g. "ta".
  const sourceLanguage = sourceLanguageOf({ language: post?.language, content: post?.content });
  // Language to translate into, or null to show the original: no ?lang=, or
  // ?lang= is already the quote's own language (validation - no API call).
  const targetLanguage = hasLangParam && quoteLanguage !== sourceLanguage ? quoteLanguage : null;
  // Translations of this quote, keyed by language code.
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const translatedText = targetLanguage ? translations[targetLanguage] ?? null : null;
  const [translating, setTranslating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUserChange = (event: Event) => {
      if (event instanceof CustomEvent) {
        setCurrentUser(event.detail ?? null);
        return;
      }
      setCurrentUser(getStoredCurrentUser());
    };

    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => {
      window.removeEventListener("inspirelens:userchange", handleUserChange);
    };
  }, []);

  useEffect(() => {
    if (!type || !postId) {
      setError("Missing post reference.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    ApiService.getPostById(type, Number(postId))
      .then((response) => {
        if (cancelled) return;
        setPost(response.post);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        const message = err.message || "Failed to load this post.";
        setError(message);
        showErrorToast(message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [type, postId]);

  useEffect(() => {
    setLocalPoints(normalizePointsCount(post?.points_count));
  }, [post?.points_count]);

  useEffect(() => {
    if (!post) {
      setVoteState(0);
      return;
    }
    if (!currentUser?.id) {
      setVoteState(0);
      return;
    }
    const userId = Number(currentUser.id);
    const hasLiked =
      Array.isArray(post.points) &&
      post.points.some((vote) => Number(vote.user_id) === userId && vote.vote_type === "up");
    setVoteState(hasLiked ? 1 : 0);
  }, [post?.points, currentUser]);

  useEffect(() => {
    cancelSpeech();
    setTranslations({});
    setIsSpeaking(false);
  }, [type, postId]);

  useEffect(() => {
    return () => cancelSpeech();
  }, []);

  // Translates the quote into the ?lang= language whenever it (or the post)
  // changes, so a shared link opens already in that language.
  useEffect(() => {
    if (!post || post.type !== "quote" || !targetLanguage || translations[targetLanguage]) {
      return;
    }
    const original = (post.content || "").trim();
    if (!original) return;

    let cancelled = false;
    setTranslating(true);
    translateText(original, targetLanguage, sourceLanguage)
      .then((translated) => {
        if (!cancelled) setTranslations((prev) => ({ ...prev, [targetLanguage]: translated }));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to translate quote:", error);
        showErrorToast(error instanceof Error ? error.message : "Unable to translate quote. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setTranslating(false);
      });

    return () => {
      cancelled = true;
      setTranslating(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, post?.type, post?.content, targetLanguage, sourceLanguage]);

  const handleReadAloud = () => {
    if (!post) return;
    const original = (post.content || "").trim();
    const textToRead = translatedText ?? original;
    if (!textToRead) return;

    if (isSpeaking) {
      cancelSpeech();
      setIsSpeaking(false);
      return;
    }

    const speechLocale = speechLocaleFor(translatedText && targetLanguage ? targetLanguage : sourceLanguage);
    speakText(textToRead, speechLocale, () => setIsSpeaking(false));
    setIsSpeaking(true);
  };

  const tagList = useMemo(() => (Array.isArray(post?.tags) ? post?.tags.filter(Boolean) : []), [post?.tags]);
  const commentList = useMemo(() => (Array.isArray(post?.comments) ? post?.comments : []), [post?.comments]);
  const hero = post ? heroMeta[post.type as keyof typeof heroMeta] || heroMeta.default : heroMeta.default;
  const accentPills = useMemo(() => highlightPills, []);
  const { theme: selectedTheme } = useTheme();
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") {
      return "light";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const appliedTheme = useMemo(() => {
    if (selectedTheme === "dark" || selectedTheme === "light") {
      return selectedTheme;
    }
    return systemTheme;
  }, [selectedTheme, systemTheme]);
  const [digestPreference, setDigestPreference] = useState<boolean | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem("inspirelens_digest_subscribed");
    if (stored === "true") return true;
    if (stored === "false") return false;
    return null;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePreferenceChange = (event: Event) => {
      const detail = (event as CustomEvent<boolean | null>).detail;
      setDigestPreference(detail ?? null);
    };
    window.addEventListener("inspirelens:digestPreferenceChanged", handlePreferenceChange);
    return () => {
      window.removeEventListener("inspirelens:digestPreferenceChanged", handlePreferenceChange);
    };
  }, []);
  const dispatchHeaderEvent = useCallback((name: string, detail?: any) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }, []);

  const handleRequireAuth = useCallback(() => {
    dispatchHeaderEvent("inspirelens:openAuth", "login");
  }, [dispatchHeaderEvent]);

  const detailHeaderProps = useMemo(
    () => ({
      currentUser,
      onOpenAuth: (mode?: string) => dispatchHeaderEvent("inspirelens:openAuth", mode),
      onOpenCreateModal: () => dispatchHeaderEvent("inspirelens:openCreateModal"),
      isSubscribed: digestPreference,
      onSubscribe: () => dispatchHeaderEvent("inspirelens:subscribe"),
      onUnsubscribe: () => dispatchHeaderEvent("inspirelens:unsubscribe"),
      isSubscriptionUpdating: false,
    }),
    [currentUser, digestPreference, dispatchHeaderEvent]
  );

  const timelineMeta = useMemo(
    () =>
      post
        ? [
            {
              label: "Shared on",
              value: formatDate(post.updated_at || post.updatedAt || post.created_at || post.createdAt),
              icon: Clock,
            },
            {
              label: "Curator",
              value: post.user || post.username || "Anonymous",
              icon: PenSquare,
            },
          ]
        : [],
    [post]
  );
  type CustomWindow = Window & {
    navigation?: {
      canGoBack: boolean;
    };
  };
  const [backward, setBackward] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const navWindow = window as CustomWindow;
    if (navWindow.navigation?.canGoBack) {
      setBackward(true);
    }
  }, []);


  const snapshotStats = useMemo(
    () => [
      { label: "Appreciations", value: formatNumber(localPoints) },
      { label: "Comments", value: formatNumber(commentList.length) },
      { label: "Category", value: post?.category || post?.category_name || "General" },
      { label: "Type", value: hero.label },
    ],
    [localPoints, commentList.length, post?.category, post?.category_name, hero.label]
  );

  const isDarkMode = appliedTheme === "dark";
  const heroBackground = "bg-[hsl(var(--background))] dark:bg-[hsl(var(--background))] text-[hsl(var(--foreground))]";
  const heroCardGradient = isDarkMode
    ? "bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-slate-800/80 text-white border-white/10"
    : "bg-gradient-to-br from-white via-slate-50 to-slate-100 text-slate-900 border-slate-200";

  const handleOpenSource = () => {
    if (post?.url) {
      window.open(post.url, "_blank", "noopener,noreferrer");
    }
  };

  // Copies the full link to this post's page, always with ?lang= so the
  // recipient sees the quote in the same language.
  const handleShare = async () => {
    const shareUrl = withLanguageParam(`${PUBLIC_SITE_URL}${window.location.pathname}${window.location.search}`);

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1400);
        showSuccessToast("Link copied to clipboard!");
      }
    } catch (clipError) {
      showErrorToast("Unable to copy the link right now.");
      console.error(clipError);
    }
  };

  const handleToggleLike = useCallback(async () => {
    if (isLiking || !post) return;
    if (!currentUser?.id) {
      handleRequireAuth();
      return;
    }

    const previousState = voteState;
    const { nextState, delta } = resolveVoteAction(previousState);

    setVoteState(nextState);
    setLocalPoints((prev: any) => prev + delta);
    setIsLiking(true);

    try {
      const response = await ApiService.vote(post.id, post.type, "upvote", Number(currentUser.id));
      const pointsPayload = Array.isArray(response) ? undefined : response?.points_count;

      if (typeof pointsPayload === "number") {
        setLocalPoints(pointsPayload);
      } else if (typeof pointsPayload === "string") {
        const parsed = parseInt(pointsPayload, 10);
        if (!Number.isNaN(parsed)) {
          setLocalPoints(parsed);
        }
      }

      if (nextState === 1) {
        showSuccessToast("Appreciation recorded!");
      } else {
        showInfoToast("Appreciation removed.");
      }
    } catch (error) {
      console.error("Failed to toggle appreciation:", error);
      setVoteState(previousState);
      setLocalPoints((prev) => prev - delta);
      showErrorToast("Failed to update appreciation. Please try again.");
    } finally {
      setIsLiking(false);
    }
  }, [currentUser, handleRequireAuth, isLiking, post, voteState]);

  const handleAddComment = useCallback(async () => {
    if (isCommentSubmitting || !post) return;
    const trimmed = commentText.trim();
    if (!trimmed) return;
    if (!currentUser?.id) {
      handleRequireAuth();
      return;
    }

    const authorName = currentUser.firstName || currentUser.name || currentUser.username || "Community";
    const optimisticComment: Comment = {
      id: Date.now(),
      comment: trimmed,
      text: trimmed,
      user: authorName,
      username: currentUser.username || currentUser.firstName || currentUser.name,
      created_at: Date.now(),
      createdAt: Date.now(),
      createdBy: currentUser.username,
      parent_id: replyTo?.id ?? null,
    };

    setPost((prev) =>
      prev
        ? { ...prev, comments: [...(Array.isArray(prev.comments) ? prev.comments : []), optimisticComment] }
        : prev
    );

    setIsCommentSubmitting(true);

    try {
      const response = await ApiService.addComment(post.id, post.type, trimmed, Number(currentUser.id), replyTo?.id);
      const serverComment = response?.comment;

      if (serverComment) {
        setPost((prev) => {
          if (!prev) return prev;
          const existingComments = Array.isArray(prev.comments)
            ? prev.comments.filter((existing) => existing.id !== optimisticComment.id)
            : [];
          return { ...prev, comments: [...existingComments, serverComment] };
        });
      }

      setCommentText("");
      setReplyTo(null);
      showSuccessToast(replyTo ? "Reply added!" : "Comment added!");
    } catch (error) {
      console.error("Failed to add comment:", error);
      setPost((prev) => {
        if (!prev) return prev;
        const filteredComments = Array.isArray(prev.comments)
          ? prev.comments.filter((existing) => existing.id !== optimisticComment.id)
          : [];
        return { ...prev, comments: filteredComments };
      });
      showErrorToast("Failed to add comment. Please try again.");
    } finally {
      setIsCommentSubmitting(false);
    }
  }, [commentText, currentUser, handleRequireAuth, isCommentSubmitting, post, replyTo]);

  // ?comment=ID (from a comment/reply/mention notification): scroll to that
  // comment and highlight it.
  const highlightedCommentId = Number(new URLSearchParams(location.search).get("comment")) || null;
  useEffect(() => {
    if (!highlightedCommentId || !post) return;
    const element = document.getElementById(`comment-${highlightedCommentId}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedCommentId, post]);

  const renderPrimaryContent = () => {
    
    if (!post) return null;

    const body = post.summary || post.content || "";
    switch (post.type) {
      case "quote":
        return (
          <blockquote className="text-2xl sm:text-3xl font-semibold text-slate-800 dark:text-slate-100 leading-relaxed italic">
            “{post.content}”
            {post.author && <span className="mt-3 block text-base font-medium text-slate-500 dark:text-slate-400">— {post.author}</span>}
          </blockquote>
        );
      case "book":
        return (
          <div className="space-y-3">
            <p className="text-xl font-semibold text-slate-900 dark:text-slate-100">{post.title}</p>
            {post.author && <p className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400">{post.author}</p>}
            <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">{body}</p>
          </div>
        );
      case "video": {
        return (
          <div className="space-y-4">
            <p className="text-xl font-semibold text-slate-900 dark:text-slate-100">{post.title}</p>
            {post.url && (
              <a href={post.url} target="_blank" rel="noopener noreferrer" className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-black/80 aspect-video flex items-center justify-center text-slate-100 text-sm">
              <div className="inline-flex items-center gap-2 underline">
            

               <Youtube color="#FF0000"  size={24}  width={80}  height={80} />
             
              </div>
                </a>
            )}
            <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">{body || "A curated video from The Deepam community."}</p>
          </div>
        );
      }
      case "article":
        return (
          <div className="space-y-4">
            <p className="text-xl font-semibold text-slate-900 dark:text-slate-100">{post.title}</p>
            <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">{body || "A featured article shared by the community."}</p>
          </div>
        );
      case "aiprompt":
        return (
          <pre className="bg-slate-900/90 text-slate-100 rounded-2xl p-4 text-sm leading-loose overflow-x-auto">
            {post.content}
          </pre>
        );
      default:
        return <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">{body}</p>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center gap-2.5 text-slate-500 dark:text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading inspiration…
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50 dark:bg-slate-900 text-center px-4">
        <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{error || "Post not found"}</p>
          <div className="flex gap-2">
            {/* This screen has no header, so it needs its own Back - shown
                whenever there's an in-app page to return to. */}
            {location.key !== "default" && (
               <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Go back
          </Button>
            )}
         
          <Button asChild>
            <Link to="/">Return to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative min-h-screen overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-500",
        heroBackground
      )}
    >
      <motion.div
        className="absolute -top-32 -right-10 md:right-0 w-72 h-72 bg-emerald-200/60 dark:bg-emerald-500/20 blur-[140px] rounded-full"
        animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 12, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 -left-16 w-80 h-80 bg-sky-200/50 dark:bg-sky-500/20 blur-[140px] rounded-full"
        animate={{ opacity: [0.3, 0.65, 0.3], scale: [0.9, 1.08, 0.9] }}
        transition={{ duration: 16, repeat: Infinity }}
      />

      <div className="relative z-10 mx-auto max-w-5xl px-3 py-6 sm:py-8 lg:py-10 space-y-6 transition-all duration-500">
    <Header {...detailHeaderProps} />

    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-2">
        {backward && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="dark:text-white dark:hover:text-white/80"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}

        <Button
          asChild
          variant="outline"
          size="sm"
          className="dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
        >
          <Link to="/">Return to Home</Link>
        </Button>
      </div>

    </div>

      <motion.div
        layout
        className={cn(
          "rounded-[40px] border border-slate-200/60 dark:border-slate-800/80 p-6 shadow-[0_20px_45px_rgba(15,23,42,0.25)] backdrop-blur-xl transition-shadow duration-500 ease-out",
          heroCardGradient
        )}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="grid gap-5 md:gap-6 lg:gap-8 md:grid-cols-[3fr_1.2fr] transition-all duration-500">
        <div className="space-y-3">
            <div className="flex items-center gap-2.5 text-xs uppercase tracking-[0.4em] text-slate-500 dark:text-slate-400">
            <div className="h-10 w-10 rounded-2xl bg-white/70 dark:bg-white/10 text-slate-900 dark:text-white shadow-lg flex items-center justify-center">
              <hero.icon className="w-5 h-5" />
            </div>
            <span className="font-semibold">{hero.label}</span>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-2xl sm:text-5xl font-semibold text-slate-900 dark:text-white">
              {post.type === "quote" && translatedText ? translatedText : post.title || post.content || post.type}
            </h3>
            {post.author && (
              <p className="mt-2 text-x1 uppercase tracking-[0.1em] text-slate-600 dark:text-slate-400">
                {post.author}
              </p>
            )}
          </div>

          {/* <p ¢ */}
          <div className="flex flex-wrap items-center gap-3">
             {post.url && (
            <Button onClick={handleOpenSource} size="sm" className="inline-flex items-center gap-2 text-sm">
              <ExternalLink className="w-4 h-4" />
              Open original
            </Button>
          )}
            {post.type === "article" && post.url && <ArticleListenButton url={post.url} title={post.title} />}
          </div>

          {tagList.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100/80 dark:border-slate-800/80">
              {tagList.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs uppercase tracking-wide">
                  #{tag}
                </Badge>
              ))}
            </div>
          )}
             <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={voteState === 1 ? "default" : "outline"}
          size="sm"
          onClick={handleToggleLike}
          disabled={isLiking}
          className={cn(
            "flex items-center gap-2",
            voteState === 1
              ? "border-orange-400 bg-orange-500/80 text-white shadow-lg hover:bg-orange-600"
              : "dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
          )}
          
        >
          <ThumbsUp className="w-4 h-4" />
          <span className="text-xs font-semibold">
            {voteState === 1 ? "Liked" : "Like"}
          </span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleShare}
          className="dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
        >
          <Share2 className="w-4 h-4 mr-1" />
          {shareCopied ? "Link copied" : "Share"}
        </Button>
        {post.type === "quote" && (
          <>
            {/* Any Google Translate language; choosing one sets ?lang= so the
                URL (and a shared link) matches what's displayed. */}
            <LanguagePicker
              value={translatedText && targetLanguage ? targetLanguage : sourceLanguage}
              originalLanguage={sourceLanguage}
              onSelect={setQuoteLanguage}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={translating}
                className="dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
              >
                {translating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Languages className="w-4 h-4 mr-1" />}
                {languageLabel(translatedText && targetLanguage ? targetLanguage : sourceLanguage)}
              </Button>
            </LanguagePicker>
            {/* <Button
              variant="outline"
              size="sm"
              onClick={handleReadAloud}
              className="dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
            >
              {isSpeaking ? <VolumeX className="w-4 h-4 mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />}
              {isSpeaking ? "Stop" : "Voice"}
            </Button> */}
          </>
        )}
      </div>

          <div className="flex flex-wrap gap-4 text-[0.65rem] font-semibold tracking-[0.4em] text-slate-600 dark:text-slate-300">
            {/* <span className="tracking-[0.5em]">
              {post.user || post.username || "Community member"}
            </span> */}
            {/* <span className="tracking-[0.5em]">
              {formatDate(post.updated_at || post.updatedAt || post.created_at || post.createdAt)}
            </span> */}
            {post.category && (
              <Badge
                variant="secondary"
                className="px-3 py-1 text-[0.6rem] uppercase tracking-[0.4em] text-slate-900 bg-white/90"
              >
                {post.category}
              </Badge>
            )}
          </div>

      {/* <div className="flex flex-wrap gap-2">
            {accentPills.map((pill, index) => (
              <motion.span
                key={`${pill.label}-${index}`}
                whileHover={{ y: -2 }}
                className={cn(
                  "flex items-center gap-1 rounded-2xl px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.25em] shadow-lg",
                  "bg-gradient-to-r",
                  pill.gradient,
                  "text-slate-900"
                )}
              >
                <pill.icon className="w-3 h-3" />
                {pill.label}
              </motion.span>
            ))}
          </div> */}
        
        </div>

      </div>
    </motion.div>

    <div className="grid gap-4 lg:gap-5 lg:grid-cols-[2fr_1fr]">
      <div className="space-y-5">
      {/* <Card className={cn(DETAIL_CARD_BASE, HERO_CARD_EXTRA)}>
        <CardContent className={DETAIL_CARD_CONTENT}>
          {renderPrimaryContent()}

          {post.url && (
            <Button onClick={handleOpenSource} size="sm" className="inline-flex items-center gap-2 text-sm">
              <ExternalLink className="w-4 h-4" />
              Open original
            </Button>
          )}

          {tagList.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100/80 dark:border-slate-800/80">
              {tagList.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs uppercase tracking-wide">
                  #{tag}
                </Badge>
              ))}
            </div>
          )}
             <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={voteState === 1 ? "default" : "outline"}
          size="sm"
          onClick={handleToggleLike}
          disabled={isLiking}
          className={cn(
            "flex items-center gap-2",
            voteState === 1
              ? "border-orange-400 bg-orange-500/80 text-white shadow-lg hover:bg-orange-600"
              : "dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
          )}
          
        >
          <ThumbsUp className="w-4 h-4" />
          <span className="text-xs font-semibold">
            {voteState === 1 ? "Liked" : "Like"}
          </span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleShare}
          className="dark:text-white dark:border-white/40 dark:hover:bg-white/10 dark:hover:text-white/80"
        >
          <Share2 className="w-4 h-4 mr-1" />
          {shareCopied ? "Link copied" : "Share"}
        </Button>
      </div>
        </CardContent>
      </Card> */}
        <Card className={cn(DETAIL_CARD_BASE, DISCUSSION_CARD_EXTRA)}>
          <CardContent className={cn(DETAIL_CARD_CONTENT, "h-full flex flex-col")}>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <MessageSquare className="w-4 h-4 text-slate-500 dark:text-slate-300" />
              Discussion ({commentList.length})
            </div>

            <div className="space-y-2">
              {replyTo && (
                <div className="flex items-center justify-between rounded-xl bg-slate-100 px-3 py-1.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <span>
                    Replying to <span className="font-semibold">{replyTo.name}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    aria-label="Cancel reply"
                    className="grid h-7 w-7 place-items-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <Textarea
                rows={3}
                placeholder={replyTo ? `Reply to ${replyTo.name}...` : "Share a thought with the community..."}
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                disabled={isCommentSubmitting}
                className="resize-none"
              />
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{currentUser ? "Your insight matters. Keep the spark going." : "Log in to leave a comment."}</span>
                <Button
                  size="sm"
                  onClick={handleAddComment}
                  disabled={isCommentSubmitting || !commentText.trim()}
                >
                  {isCommentSubmitting ? "Posting..." : replyTo ? "Post reply" : "Post comment"}
                </Button>
              </div>
            </div>

            {commentList.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No comments yet. Be the first to spark a discussion from the main feed.
              </p>
            ) : (
              <ScrollArea className="h-56 pr-3">
                <div className="space-y-3">
                  {commentList.map((comment) => {
                    const authorName = comment.username || comment.user || "Community";
                    const parent = comment.parent_id
                      ? commentList.find((candidate) => candidate.id === comment.parent_id)
                      : null;
                    return (
                      <motion.div
                        key={comment.id}
                        id={`comment-${comment.id}`}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={cn(
                          "p-3 rounded-2xl bg-slate-100 dark:bg-slate-800/70",
                          Boolean(comment.parent_id) && "ml-4",
                          comment.id === highlightedCommentId && "ring-2 ring-inset ring-amber-400"
                        )}
                      >
                        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                          <span className="font-semibold text-slate-700 dark:text-slate-200">{authorName}</span>
                          {/* <span>{formatDate(comment.created_at || comment.createdAt)}</span> */}
                        </div>
                        {parent && (
                          <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">
                            Replying to {parent.username || parent.user || "Community"}
                          </p>
                        )}
                        <p className="text-sm text-slate-700 dark:text-slate-200">
                          {comment.comment || comment.text}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            if (!currentUser?.id) {
                              handleRequireAuth();
                              return;
                            }
                            setReplyTo({ id: comment.id, name: authorName });
                          }}
                          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          <Reply className="h-3.5 w-3.5" /> Reply
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
        </div>

      <div className="space-y-4">
        <Card className={cn(DETAIL_CARD_BASE, SNAPSHOT_CARD_EXTRA)}>
          <CardContent className={DETAIL_CARD_CONTENT}>
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Snapshot
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {snapshotStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl bg-slate-100/80 dark:bg-slate-800/60 p-3 shadow-inner shadow-white/20 dark:shadow-black/20"
                >
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {stat.label}
                  </p>
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className={cn(DETAIL_CARD_BASE, STORY_CARD_EXTRA)}>
          <CardContent className={DETAIL_CARD_CONTENT}>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <Clock className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              Storyline
            </div>
            <div className="space-y-2">
              {timelineMeta.map((meta) => (
                <motion.div
                  key={meta.label}
                  whileHover={{ x: 4 }}
                  className="flex items-center gap-2 rounded-2xl bg-slate-100/80 dark:bg-slate-800/60 px-3 py-1.5 text-sm"
                >
                  <div className="rounded-xl bg-white text-slate-900 dark:bg-slate-900 dark:text-white p-1.5 shadow">
                    <meta.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {meta.label}
                    </p>
                    <p className="font-semibold text-slate-900 dark:text-white">{meta.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
{/* 
        <Card className={cn(DETAIL_CARD_BASE, DISCUSSION_CARD_EXTRA)}>
          <CardContent className={cn(DETAIL_CARD_CONTENT, "h-full flex flex-col")}>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <MessageSquare className="w-4 h-4 text-slate-500 dark:text-slate-300" />
              Discussion ({commentList.length})
            </div>

            <div className="space-y-2">
              <Textarea
                rows={3}
                placeholder="Share a thought with the community..."
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                disabled={isCommentSubmitting}
                className="resize-none"
              />
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{currentUser ? "Your insight matters. Keep the spark going." : "Log in to leave a comment."}</span>
                <Button
                  size="sm"
                  onClick={handleAddComment}
                  disabled={isCommentSubmitting || !commentText.trim()}
                >
                  {isCommentSubmitting ? "Posting..." : "Post comment"}
                </Button>
              </div>
            </div>

            {commentList.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No comments yet. Be the first to spark a discussion from the main feed.
              </p>
            ) : (
              <ScrollArea className="h-56 pr-3">
                <div className="space-y-3">
                  {commentList.map((comment) => (
                    <motion.div
                      key={comment.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800/70"
                    >
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                        <span className="font-semibold text-slate-700 dark:text-slate-200">
                          {comment.username || comment.user || "Community"}
                        </span>
                        <span>{formatDate(comment.created_at || comment.createdAt)}</span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-200">
                        {comment.comment || comment.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card> */}
      </div>
    </div>
  </div>
</div>

  );
};

export default PostDetailPage;
